import { useEffect, useState, useRef, useCallback, useMemo, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import {
  LogIn, LogOut, MapPin, Clock, Navigation as NavigationIcon, ClipboardList, Star,
  Map as MapIcon, Target, BarChart3, SlidersHorizontal, Fuel, Trophy, Menu, Settings,
  Euro, Car, Truck, Store, RotateCcw, AlertTriangle, TrendingUp, Zap, Smartphone,
  CheckCircle2, XCircle, Info, User, Check, Hand, Search, Share2,
  TrendingDown, Calendar, CalendarClock
} from "lucide-react";
import { createClient } from '@supabase/supabase-js';
import FaqSection from './FaqSection';
import cities from './data/cities';
import "./App.css";

// Die größten Städte für die Footer-Verlinkung (interne Linkstruktur)
const topCities = cities.slice(0, 12);

// Nebenrouten werden erst bei Bedarf nachgeladen - die Startseite ist der
// Einstiegspunkt für Suchmaschinen und Anzeigen und soll nicht den Code
// von Blog, Städteseiten und Rechtstexten mitschleppen.
const Datenschutz = lazy(() => import('./Datenschutz'));
const Impressum = lazy(() => import('./Impressum'));
const BlogList = lazy(() => import('./blog/BlogList'));
const BlogPost = lazy(() => import('./blog/BlogPost'));
const CityIndex = lazy(() => import('./CityIndex'));
const CityPage = lazy(() => import('./CityPage'));

// Supabase Client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || process.env.GOOGLE_APP_SUPABASE;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || process.env.GOOGLE_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Fix für Leaflet Icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const TANKERKOENIG_API_KEY = process.env.REACT_APP_TANKERKOENIG_API_KEY;

const FUEL_META = {
  e5: { label: 'Super E5', color: '#10b981' },
  e10: { label: 'Super E10', color: '#3b82f6' },
  diesel: { label: 'Diesel', color: '#f59e0b' },
};

// Preisverlauf-Tooltip im Stil einer echten Tankstellen-Preistafel
// (dunkler Hintergrund, große LED-artige Ziffern) statt der generischen
// weißen Recharts-Standardbox mit Großbuchstaben-Label.
function TankboardTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="tankboard-tooltip">
      <div className="tankboard-tooltip-date">{label}</div>
      {payload.map((entry) => {
        const meta = FUEL_META[entry.dataKey] || { label: entry.name, color: entry.color };
        return (
          <div className="tankboard-tooltip-row" key={entry.dataKey}>
            <span className="tankboard-tooltip-label" style={{ color: meta.color }}>{meta.label}</span>
            <span className="tankboard-tooltip-price">
              {entry.value}<small>€</small>
            </span>
          </div>
        );
      })}
    </div>
  );
}

function MapViewUpdater({ center, zoom, locationKey }) {
  const map = useMap();
  const [lastLocationKey, setLastLocationKey] = useState(null);
  
  useEffect(() => {
    // Only update map view when location changes (new search)
    if (locationKey !== lastLocationKey) {
      map.setView(center, zoom);
      setLastLocationKey(locationKey);
    }
  }, [center, map, zoom, locationKey, lastLocationKey]);
  
  return null;
}

function DynamicRadiusUpdater({ onRadiusChange }) {
  const map = useMap();
  
  useEffect(() => {
    const handleZoomEnd = () => {
      const currentZoom = map.getZoom();
      let newRadius;
      
      // Dynamic radius based on zoom level - more intuitive mapping
      if (currentZoom >= 15) {
        newRadius = 1; // Very close zoom - very small radius
      } else if (currentZoom >= 14) {
        newRadius = 2; // Close zoom - small radius  
      } else if (currentZoom >= 13) {
        newRadius = 5; // Default zoom - default radius
      } else if (currentZoom >= 12) {
        newRadius = 10; // Medium zoom - increased from 8 to 10
      } else if (currentZoom >= 11) {
        newRadius = 15; // Far zoom
      } else if (currentZoom >= 10) {
        newRadius = 25; // Very far zoom
      } else if (currentZoom >= 9) {
        newRadius = 35; // Extremely far zoom
      } else {
        newRadius = 50; // Maximum zoom out - maximum radius (50km limit)
      }
      
      // Only suggest radius change, don't change map view
      onRadiusChange(newRadius);
    };

    map.on('zoomend', handleZoomEnd);
    
    return () => {
      map.off('zoomend', handleZoomEnd);
    };
  }, [map, onRadiusChange]);

  return null;
}

function MainApp() {
  const [location, setLocation] = useState(null);
  const [stations, setStations] = useState([]);
  const [radius, setRadius] = useState(5);
  const [tempRadius, setTempRadius] = useState(5);

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [useGPS, setUseGPS] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedStation, setSelectedStation] = useState(null);
  const [stationDetails, setStationDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [priceHistory, setPriceHistory] = useState([]);
  const [priceHistoryCache, setPriceHistoryCache] = useState({}); // Cache für alle Stationen
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [session, setSession] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [sortBy, setSortBy] = useState("relevance");
  const [showOpenOnly, setShowOpenOnly] = useState(false);
  const [priceAlert, setPriceAlert] = useState(null);
  const [showCards, setShowCards] = useState(false);
  const [showMobileRadiusControl, setShowMobileRadiusControl] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [favoriteStations, setFavoriteStations] = useState([]);
  const [showMobileFilters, setShowMobileFilters] = useState(true);
  const [searchFocused, setSearchFocused] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipedStations, setSwipedStations] = useState(new Set());
  const [lastSearchedPostalCode, setLastSearchedPostalCode] = useState(null);
  const [toast, setToast] = useState(null);
  const [showMapFirst, setShowMapFirst] = useState(false);
  const [mobileTab, setMobileTab] = useState('search'); // 'search' | 'map' | 'favorites'
  const [lastUpdated, setLastUpdated] = useState(null);
  const [lastSearchText, setLastSearchText] = useState(""); // For placeholder
  const [suggestedRadius, setSuggestedRadius] = useState(null); // Suggested radius from zoom
  // Create a unique key for location changes to trigger map updates only when location changes
  const locationKey = location ? `${location.lat.toFixed(4)}_${location.lng.toFixed(4)}` : null;
  const searchRef = useRef(null);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // Update document title based on app state
  useEffect(() => {
    if (location && stations.length > 0) {
      document.title = `${stations.length} Tankstellen gefunden - Tankgünstig.de`;
    } else if (location) {
      document.title = `Suche läuft... - Tankgünstig.de`;
    } else {
      // Keyword zuerst: Das ist der Titel, den Google in den Ergebnissen zeigt
      document.title = `Benzinpreise vergleichen & günstig tanken | Tankgünstig.de`;
    }
  }, [location, stations.length]);

  // Supabase Auth und Favoriten
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    const savedSearches = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    setRecentSearches(savedSearches);

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session) {
      loadFavorites();
    }
  }, [session]);

  // PWA-Installationshinweis: Chrome/Edge/Android feuern dieses Event nur,
  // wenn die App installierbar ist (Manifest + Service Worker korrekt).
  // Safari/iOS unterstützt das Event nicht - dort bleibt der Banner aus.
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      if (localStorage.getItem('installBannerDismissed') === 'true') return;
      setInstallPrompt(e);
      setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
    setShowInstallBanner(false);
  };

  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('installBannerDismissed', 'true');
  };

  const loadFavorites = async () => {
    const { data, error } = await supabase
      .from('favorites')
      .select('station_id')
      .eq('user_id', session.user.id);

    if (!error) {
      setFavorites(data.map(fav => fav.station_id));
    }
  };

  // Favoriten-Stationen laden
  const loadFavoriteStations = async () => {
    if (!session || favorites.length === 0) {
      setFavoriteStations([]);
      return;
    }

    try {
      const stationPromises = favorites.map(async (stationId) => {
        const response = await fetch(
          `https://creativecommons.tankerkoenig.de/json/detail.php?id=${stationId}&apikey=${TANKERKOENIG_API_KEY}`
        );
        const data = await response.json();
        return data.station;
      });

      const stations = await Promise.all(stationPromises);
      setFavoriteStations(stations.filter(station => station));
    } catch (error) {
      console.error('Fehler beim Laden der Favoriten:', error);
      showToast("Aktuell keine Verbindung zum Server", "error");
    }
  };

  useEffect(() => {
    if (session && favorites.length > 0) {
      loadFavoriteStations();
    } else {
      setFavoriteStations([]);
    }
  }, [session, favorites]);

  const toggleFavorite = async (stationId) => {
    if (!session) {
      showToast('Bitte einloggen um Favoriten zu speichern', 'error');
      return;
    }

    const isFavorite = favorites.includes(stationId);
    const station = stations.find(s => s.id === stationId) || favoriteStations.find(s => s.id === stationId);
    const stationName = station?.name || 'Tankstelle';

    if (isFavorite) {
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', session.user.id)
        .eq('station_id', stationId);

      if (!error) {
        setFavorites(favorites.filter(id => id !== stationId));
        showToast(`${stationName} aus Favoriten entfernt`, 'info');
      }
    } else {
      const { error } = await supabase
        .from('favorites')
        .insert([{
          user_id: session.user.id,
          station_id: stationId,
          added_at: new Date().toISOString()
        }]);

      if (!error) {
        setFavorites([...favorites, stationId]);
        showToast(`${stationName} zu Favoriten hinzugefügt`, 'success');
      }
    }
  };

  const loadRecentSearches = useCallback(() => {
    const savedSearches = JSON.parse(localStorage.getItem('recentSearches') || '[]');
    setRecentSearches(savedSearches);
  }, []);

  const resetToHome = useCallback(() => {
    setLocation(null);
    setStations([]);
    setQuery("");
    setLastSearchText(""); // Reset placeholder
    setSuggestions([]);
    setUseGPS(false);
    setSelectedStation(null);
    loadRecentSearches();
  }, [loadRecentSearches]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const shareStation = async (station) => {
    const priceText = [
      station.e5 > 0 ? `E5: ${station.e5.toFixed(3)} €` : null,
      station.e10 > 0 ? `E10: ${station.e10.toFixed(3)} €` : null,
      station.diesel > 0 ? `Diesel: ${station.diesel.toFixed(3)} €` : null,
    ].filter(Boolean).join(' · ');

    const shareText = `${station.name}\n${station.street}, ${station.place}\n${priceText}\n\nGefunden mit Tankgünstig.de`;
    const shareUrl = 'https://tankgünstig.de/';

    if (navigator.share) {
      try {
        await navigator.share({ title: station.name, text: shareText, url: shareUrl });
      } catch (err) {
        // Nutzer hat den Teilen-Dialog abgebrochen - kein Fehler
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      showToast('Tankstelle in die Zwischenablage kopiert', 'success');
    } catch {
      showToast('Teilen wird von diesem Browser nicht unterstützt', 'error');
    }
  };

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = (stationId) => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isRightSwipe && session) {
      toggleFavorite(stationId);
      setSwipedStations(prev => new Set([...prev, stationId]));
      setTimeout(() => {
        setSwipedStations(prev => {
          const newSet = new Set(prev);
          newSet.delete(stationId);
          return newSet;
        });
      }, 1000);
    }
  };

  const getBrowserLocation = useCallback(() => {
    setLoading(true);
    setError("");
    setUseGPS(true);

    if (!navigator.geolocation) {
      setError("Geolocation wird von Ihrem Browser nicht unterstützt.");
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const newLocation = { lat: latitude, lng: longitude };
        setLocation(newLocation);
        const displayText = `Ihr Standort (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`;
        setLastSearchText(displayText); // Store for placeholder
        setQuery(""); // Clear the input field

        updateRecentSearches({
          display_name: displayText,
          lat: latitude.toString(),
          lon: longitude.toString()
        });

        setSuggestions([]);
        setLoading(false);
      },
      (err) => {
        setError("Standort konnte nicht ermittelt werden. Stellen Sie sicher, dass die Standortfreigabe aktiviert ist.");
        setLoading(false);
        console.error(err);
      }
    );
  }, []);

  const updateRecentSearches = (suggestion) => {
    const newSearch = {
      ...suggestion,
      timestamp: Date.now()
    };

    const updatedSearches = [
      newSearch,
      ...recentSearches.filter(s =>
        s.display_name !== suggestion.display_name
      ).slice(0, 4)
    ];

    setRecentSearches(updatedSearches);
    localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
  };

  const cheapestPrice = (station) => Math.min(station.e5 || 999, station.e10 || 999, station.diesel || 999);

  // Grober Aufpreis pro Extra-Kilometer, den ein weiterer Weg "kosten" darf,
  // bevor er einen niedrigeren Preis nicht mehr aufwiegt. 3 Cent/km heißt:
  // ein 30-km-Umweg müsste über 90 Cent günstiger sein, um sich noch vor
  // eine nahe Station zu schieben - verhindert genau den Fall "billigster
  // Preis, aber 30 km entfernt" aus dem Briefing.
  const RELEVANCE_KM_PENALTY = 0.03;
  const relevanceScore = (station) => cheapestPrice(station) + station.dist * RELEVANCE_KM_PENALTY;

  // Tankstellen filtern und sortieren (Memoized for immediate sync)
  const filteredStations = useMemo(() => {
    let filtered = [...stations]; // Create a copy to avoid mutation

    if (showFavorites) {
      filtered = filtered.filter(station => favorites.includes(station.id));
    }

    if (showOpenOnly) {
      filtered = filtered.filter(station => station.isOpen !== false);
    }

    // Sortierung
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "relevance":
          return relevanceScore(a) - relevanceScore(b);
        case "price":
          return cheapestPrice(a) - cheapestPrice(b);
        case "e5":
          return (a.e5 || 999) - (b.e5 || 999);
        case "e10":
          return (a.e10 || 999) - (b.e10 || 999);
        case "diesel":
          return (a.diesel || 999) - (b.diesel || 999);
        case "name":
          return a.name.localeCompare(b.name);
        case "distance":
          return a.dist - b.dist;
        default:
          return relevanceScore(a) - relevanceScore(b);
      }
    });

    return filtered;
  }, [stations, showFavorites, favorites, sortBy, showOpenOnly]);

  useEffect(() => {
    if (query.length < 3 || useGPS) {
      setSuggestions([]);
      return;
    }

    const delayDebounce = setTimeout(() => {
      fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=de&limit=5`
      )
        .then((res) => res.json())
        .then((data) => {
          setSuggestions(data);
          setUseGPS(false);
        })
        .catch(() => {
          setSuggestions([]);
        });
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [query, useGPS]);

  useEffect(() => {
    if (!location) return;

    setLoading(true);
    setShowCards(false);

    // Fail fast instead of hanging on "Lade Tankstellen..." indefinitely
    // when the Tankerkoenig API is unreachable/down.
    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 12000);

    fetch(
      `https://creativecommons.tankerkoenig.de/json/list.php?lat=${location.lat}&lng=${location.lng}&rad=${Math.min(
        radius,
        50
      )}&sort=dist&type=all&apikey=${TANKERKOENIG_API_KEY}`,
      { signal: controller.signal }
    )
      .then((res) => res.json())
      .then((data) => {
        setStations(data.stations || []);
        setLastUpdated(new Date());
        setLoading(false);
        setError("");
        setShowCards(true);
      })
      .catch((err) => {
        // A new search (location/radius change) cancels the previous
        // request via cleanup below - that's not a real error, ignore it.
        if (err.name === 'AbortError' && !timedOut) return;
        console.error(err);
        setLoading(false);
        resetToHome();
        showToast(
          timedOut
            ? "Der Server antwortet gerade nicht. Bitte versuchen Sie es später erneut."
            : "Aktuell keine Verbindung zum Server",
          "error"
        );
      })
      .finally(() => clearTimeout(timeoutId));

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [location, radius]);

  // Preload price history for the few closest stations in the background,
  // so the chart is instant if the user opens one of them. Capped at 3
  // (not the whole list) and every scheduled timeout is tracked and
  // cleared on cleanup - a new search used to leave orphaned preload
  // requests for the previous, no-longer-relevant station list running.
  useEffect(() => {
    if (stations.length === 0) return;

    const timeoutIds = stations.slice(0, 3).map((station, i) =>
      setTimeout(() => preloadPriceHistory(station.id), 800 + i * 300)
    );

    return () => timeoutIds.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stations]);

  // Shared by loadPriceHistory (visible, for the open modal) and
  // preloadPriceHistory (silent background warm-up) - was previously
  // duplicated in both places and had already drifted slightly out of sync.
  const fetchPriceHistoryFromDB = async (stationId) => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const { data, error } = await supabase
      .from('fuel_prices')
      .select('date, diesel, e5, e10')
      .eq('station_uuid', stationId)
      .gte('date', sevenDaysAgo.toISOString().split('T')[0])
      .order('date', { ascending: true });

    if (error) throw error;
    if (!data || data.length === 0) return [];

    // Group by date and take average if multiple entries per day
    const groupedData = {};
    data.forEach(item => {
      const dateKey = item.date.split('T')[0];
      if (!groupedData[dateKey]) {
        groupedData[dateKey] = { date: dateKey, e5: [], e10: [], diesel: [] };
      }
      if (item.e5 && item.e5 > 0) groupedData[dateKey].e5.push(parseFloat(item.e5));
      if (item.e10 && item.e10 > 0) groupedData[dateKey].e10.push(parseFloat(item.e10));
      if (item.diesel && item.diesel > 0) groupedData[dateKey].diesel.push(parseFloat(item.diesel));
    });

    const avg = (arr) => (arr.length > 0 ? (arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(3) : null);

    return Object.values(groupedData).map(item => ({
      day: new Date(item.date).toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'numeric' }),
      date: item.date,
      e5: avg(item.e5),
      e10: avg(item.e10),
      diesel: avg(item.diesel),
    }));
  };

  const loadPriceHistory = async (stationId) => {
    if (priceHistoryCache[stationId]) {
      setPriceHistory(priceHistoryCache[stationId]);
      setLoadingHistory(false);
      return;
    }

    setLoadingHistory(true);
    try {
      const formattedData = await fetchPriceHistoryFromDB(stationId);
      setPriceHistory(formattedData);
      setPriceHistoryCache(prev => ({ ...prev, [stationId]: formattedData }));
    } catch (err) {
      console.error('Fehler beim Laden der Preishistorie:', err);
      setPriceHistory([]);
      // Leeres Ergebnis NICHT cachen - ein Netzwerkfehler soll beim
      // nächsten Öffnen der Station erneut versucht werden, statt
      // dauerhaft "keine Historie" vorzutäuschen.
    } finally {
      setLoadingHistory(false);
    }
  };

  // Silent background warm-up so the chart appears instantly once a user
  // actually opens a station - see fetchPriceHistoryFromDB for the shared
  // fetch/format logic (previously duplicated here almost verbatim).
  const preloadPriceHistory = async (stationId) => {
    if (priceHistoryCache[stationId]) return;

    try {
      const formattedData = await fetchPriceHistoryFromDB(stationId);
      setPriceHistoryCache(prev => ({ ...prev, [stationId]: formattedData }));
    } catch (err) {
      // Preload ist rein optional - bei Fehlschlag einfach nichts cachen,
      // damit ein späterer echter Öffnen-Versuch es erneut probiert.
    }
  };

  // Preisstatistiken berechnen (Memoized)
  const priceStats = useMemo(() => {
    const stats = {
      e5: { prices: [], min: 999, max: 0 },
      e10: { prices: [], min: 999, max: 0 },
      diesel: { prices: [], min: 999, max: 0 }
    };

    filteredStations.forEach(station => {
      ['e5', 'e10', 'diesel'].forEach(fuelType => {
        const price = station[fuelType];
        if (price && price > 0) {
          stats[fuelType].prices.push(price);
          stats[fuelType].min = Math.min(stats[fuelType].min, price);
          stats[fuelType].max = Math.max(stats[fuelType].max, price);
        }
      });
    });

    const calculateFuelStats = (fuelData, fuelType) => {
      if (fuelData.prices.length === 0) return { avg: "—", min: "—", max: "—", savings: "—", cheapest: "—", range: "—" };

      const sorted = fuelData.prices.sort((a, b) => a - b);
      const avg = (fuelData.prices.reduce((a, b) => a + b, 0) / fuelData.prices.length);
      const cheapestStation = filteredStations.find(s => s[fuelType] === fuelData.min);

      return {
        avg: avg.toFixed(3),
        min: fuelData.min.toFixed(3),
        max: fuelData.max.toFixed(3),
        savings: ((avg - fuelData.min) * 50).toFixed(2),
        cheapest: cheapestStation?.name?.substring(0, 20) + (cheapestStation?.name?.length > 20 ? '...' : '') || "—",
        range: `${(fuelData.max - fuelData.min).toFixed(3)} €`
      };
    };

    return {
      e5: calculateFuelStats(stats.e5, 'e5'),
      e10: calculateFuelStats(stats.e10, 'e10'),
      diesel: calculateFuelStats(stats.diesel, 'diesel'),
      totalStations: filteredStations.length,
      openStations: filteredStations.filter(s => s.isOpen !== false).length
    };
  }, [filteredStations]);

  // Günstigste Tankstellen für jede Sorte ermitteln (Memoized)
  const cheapestStations = useMemo(() => {
    if (filteredStations.length === 0) return { e5: null, e10: null, diesel: null };

    let cheapest = {
      e5: { station: null, price: 999, count: 0 },
      e10: { station: null, price: 999, count: 0 },
      diesel: { station: null, price: 999, count: 0 }
    };

    filteredStations.forEach(station => {
      // E5
      if (station.e5 > 0) {
        if (station.e5 < cheapest.e5.price) {
          cheapest.e5 = { station, price: station.e5, count: 1 };
        } else if (station.e5 === cheapest.e5.price) {
          cheapest.e5.count++;
        }
      }
      // E10
      if (station.e10 > 0) {
        if (station.e10 < cheapest.e10.price) {
          cheapest.e10 = { station, price: station.e10, count: 1 };
        } else if (station.e10 === cheapest.e10.price) {
          cheapest.e10.count++;
        }
      }
      // Diesel
      if (station.diesel > 0) {
        if (station.diesel < cheapest.diesel.price) {
          cheapest.diesel = { station, price: station.diesel, count: 1 };
        } else if (station.diesel === cheapest.diesel.price) {
          cheapest.diesel.count++;
        }
      }
    });

    return {
      e5: cheapest.e5.count === 1 ? cheapest.e5.station : null,
      e10: cheapest.e10.count === 1 ? cheapest.e10.station : null,
      diesel: cheapest.diesel.count === 1 ? cheapest.diesel.station : null
    };
  }, [filteredStations]);

  // Preisalarm prüfen
  const checkPriceAlert = (station) => {
    if (!priceAlert) return false;
    // Check if any fuel price is below the alert threshold
    return (station.e5 && station.e5 <= priceAlert) ||
      (station.e10 && station.e10 <= priceAlert) ||
      (station.diesel && station.diesel <= priceAlert);
  };

  const selectSuggestion = (s) => {
    const newLocation = { lat: parseFloat(s.lat), lng: parseFloat(s.lon) };
    setLocation(newLocation);
    // Keep the search text as placeholder, but clear the input value
    const searchText = s.display_name;
    setLastSearchText(searchText); // Store for placeholder
    setQuery(""); // Clear the input field
    setSuggestions([]);
    setUseGPS(false);

    // Extract postal code from display_name
    const postalCodeMatch = s.display_name.match(/\b\d{5}\b/);
    if (postalCodeMatch) {
      setLastSearchedPostalCode(postalCodeMatch[0]);
    }

    updateRecentSearches({
      ...s,
      display_name: searchText
    });
  };

  // Helper function to decode applicable_days bitmask
  const decodeApplicableDays = (dayCode) => {
    const days = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
    const result = [];
    
    // Handle different encoding schemes
    if (dayCode === 31) {
      // 31 = 11111 (binary) = Monday to Friday (weekdays)
      return ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag'];
    } else if (dayCode === 32) {
      // 32 = 100000 (binary) = Saturday
      return ['Samstag'];
    } else if (dayCode === 64) {
      // 64 = 1000000 (binary) = Sunday  
      return ['Sonntag'];
    } else if (dayCode === 96) {
      // 96 = 1100000 (binary) = Saturday + Sunday (weekend)
      return ['Samstag', 'Sonntag'];
    } else if (dayCode === 127) {
      // 127 = 1111111 (binary) = All days
      return days;
    } else {
      // Standard bitmask decoding (bit 0 = Monday, bit 1 = Tuesday, etc.)
      for (let i = 0; i < 7; i++) {
        if (dayCode & (1 << i)) {
          result.push(days[i]);
        }
      }
    }
    
    return result.length > 0 ? result : [`Code ${dayCode}`];
  };

  // Helper function to parse opening times JSON
  const parseOpeningTimes = (openingTimesJson) => {
    if (!openingTimesJson) {
      return [];
    }
    
    // Handle if it's already an object or if it's a string that needs parsing
    let parsedJson = openingTimesJson;
    if (typeof openingTimesJson === 'string') {
      try {
        parsedJson = JSON.parse(openingTimesJson);
      } catch (e) {
        console.error('Failed to parse opening times JSON:', e);
        return [];
      }
    }
    
    if (!parsedJson.openingTimes || !Array.isArray(parsedJson.openingTimes)) {
      return [];
    }
    
    const parsedTimes = [];
    
    parsedJson.openingTimes.forEach((timeEntry) => {
      if (timeEntry.periods && timeEntry.applicable_days !== undefined) {
        const days = decodeApplicableDays(timeEntry.applicable_days);
        
        timeEntry.periods.forEach((period) => {
          days.forEach((day) => {
            parsedTimes.push({
              text: day,
              day: day,
              start: period.startp,
              end: period.endp,
              hours: `${period.startp} - ${period.endp}`
            });
          });
        });
      }
    });
    
    // Sort by day order
    const dayOrder = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
    parsedTimes.sort((a, b) => dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day));
    
    return parsedTimes;
  };

  // Helper function to get current day name in German
  const getCurrentDayName = () => {
    const days = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    return days[new Date().getDay()];
  };

  // Helper function to check if a day matches current day
  const isCurrentDay = (dayText) => {
    if (!dayText) return false;
    const currentDay = getCurrentDayName();
    return dayText.toLowerCase().includes(currentDay.toLowerCase()) || 
           dayText.toLowerCase().includes(currentDay.substring(0, 2).toLowerCase());
  };

  // Fetch detailed station information from Supabase
  const fetchStationDetails = async (stationId) => {
    setLoadingDetails(true);
    try {
      const { data, error } = await supabase
        .from('stations')
        .select('uuid, name, brand, street, house_number, post_code, city, latitude, longitude, first_active, openingtimes_json, created_at, updated_at')
        .eq('uuid', stationId)
        .single();
      
      if (error) {
        console.error('Supabase error fetching station details:', error);
        setStationDetails(null);
      } else if (data) {
        // Transform the data to match the expected format
        const transformedData = {
          id: data.uuid,
          name: data.name,
          brand: data.brand,
          street: data.street,
          houseNumber: data.house_number,
          postCode: data.post_code,
          place: data.city,
          lat: data.latitude,
          lng: data.longitude,
          firstActive: data.first_active,
          openingTimes: parseOpeningTimes(data.openingtimes_json),
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          // Additional computed fields
          wholeDay: false, // We can determine this from openingTimes if needed
          state: null, // Not available in your schema
          overrides: [] // Not available in your schema
        };
        
        setStationDetails(transformedData);
      } else {
        console.log('No station details found for ID:', stationId);
        setStationDetails(null);
      }
    } catch (error) {
      console.error('Error fetching station details from Supabase:', error);
      setStationDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const selectStation = (station) => {
    setSelectedStation(station);
    setStationDetails(null); // Reset previous details
    fetchStationDetails(station.id); // Fetch detailed information
    loadPriceHistory(station.id); // Checks the cache internally, loads if needed

    // Switch to search tab on mobile to ensure the modal is visible
    setMobileTab('search');
  };

  const closeStationDetail = () => {
    setSelectedStation(null);
    setStationDetails(null);
    setPriceHistory([]);
  };

  // Enhanced price comparison logic for better readability
  const getPriceComparison = (stationPrice, fuelType) => {
    if (!stationPrice || !priceStats[fuelType] || priceStats[fuelType].avg === "—") return null;

    const avgPrice = parseFloat(priceStats[fuelType].avg);
    const difference = stationPrice - avgPrice;
    const isCheaper = difference < 0;

    return {
      difference: Math.abs(difference).toFixed(3),
      isCheaper,
      percentage: avgPrice > 0 ? Math.abs((difference / avgPrice) * 100).toFixed(1) : 0
    };
  };

  const handleDynamicRadiusChange = useCallback((newRadius) => {
    if (newRadius !== radius && newRadius !== suggestedRadius) {
      // Just set the suggested radius, no popup
      setSuggestedRadius(newRadius);
    }
  }, [radius, suggestedRadius]);

  // Accept the suggested radius
  const acceptSuggestedRadius = useCallback(() => {
    if (suggestedRadius) {
      setRadius(suggestedRadius);
      setTempRadius(suggestedRadius);
      setSuggestedRadius(null);
      showToast(`Suchradius auf ${suggestedRadius} km angepasst`, 'success');
    }
  }, [suggestedRadius]);

  const handleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
    });
    if (error) console.error('Login error:', error);
  };

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) console.error('Logout error:', error);
  };

  return (
    <div className="app-container">
      {/* HEADER + SEARCH (merge into one row on desktop) */}
      <div className="header-search-row">
      <header className="header">
        <div className="header-content">
          <div className="logo-section">
            <div className="logo" onClick={resetToHome}>
              <img src="/logo.png" alt="TANKGÜNSTIG" className="logo-image" />
            </div>
            <div className="tagline-container">
              <p className="tagline">Günstig tanken in Echtzeit</p>
              {lastUpdated && (
                <p className="update-time">
                  Stand: {lastUpdated.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
                </p>
              )}
            </div>
          </div>

          <div className="auth-section hide-mobile">
            {session ? (
              <div className="user-menu" onClick={() => setShowProfileModal(true)}>
                <div className="user-avatar">
                  {session.user.user_metadata?.avatar_url ? (
                    <>
                      <img
                        src={session.user.user_metadata.avatar_url}
                        alt="Avatar"
                        className="logo-image"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="avatar-fallback" style={{ display: 'none' }}>
                        {session.user.email?.charAt(0).toUpperCase()}
                      </div>
                    </>
                  ) : (
                    <div className="avatar-fallback">
                      {session.user.email?.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="user-details">
                  <span className="user-name">{session.user.user_metadata?.full_name || 'Angemeldet'}</span>
                  <span className="user-email">{session.user.email}</span>
                </div>
                <button className="logout-btn" onClick={(e) => { e.stopPropagation(); handleLogout(); }}>
                  <span className="logout-icon"><LogOut size={16} strokeWidth={2.5} /></span>
                  Abmelden
                </button>
              </div>
            ) : (
              <button className="login-btn" onClick={handleLogin}>
                <span className="login-icon"><LogIn size={18} strokeWidth={2.5} /></span>
                <span className="login-text">
                  <span className="login-main">Anmelden</span>
                  <span className="login-sub">Favoriten speichern</span>
                </span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* HERO SEARCH */}
      {mobileTab !== 'map' && (
        <section className={`hero-search ${!location ? 'hero-search-landing' : ''}`}>
          <div className="search-wrapper">
            <div className="search-container">
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setUseGPS(false);
                }}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                placeholder={lastSearchText || "Stadt, PLZ oder Adresse eingeben - z.B. Berlin, 10115, Hauptstraße 1..."}
                className="search-input"
              />
              <button
                className="gps-btn"
                onClick={getBrowserLocation}
                title="Aktuellen Standort verwenden"
              >
                <MapPin size={20} strokeWidth={2.5} />
              </button>
            </div>

            {searchFocused && query.length === 0 && recentSearches.length > 0 && (
              <div className="recent-searches">
                <div className="recent-searches-title">Zuletzt gesucht</div>
                {recentSearches.map((search, index) => (
                  <div
                    key={search.place_id || index}
                    className="recent-search-item"
                    onClick={() => selectSuggestion(search)}
                  >
                    <div className="recent-search-icon"><Clock size={16} /></div>
                    <div className="recent-search-text">{search.display_name}</div>
                    <div className="recent-search-arrow">→</div>
                  </div>
                ))}
              </div>
            )}

            {searchFocused && suggestions.length > 0 && (
              <div className="suggestions-dropdown">
                {suggestions.map((s) => (
                  <div
                    key={s.place_id}
                    className="suggestion-item"
                    onClick={() => selectSuggestion(s)}
                  >
                    <div className="suggestion-icon"><MapPin size={16} /></div>
                    <div className="suggestion-text">{s.display_name}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}
        </section>
      )}
      </div>

      {/* PWA INSTALL BANNER */}
      {showInstallBanner && (
        <div className="install-banner">
          <div className="install-banner-text">
            <Smartphone size={18} />
            <span>Tankgünstig als App installieren – schneller Zugriff vom Startbildschirm</span>
          </div>
          <div className="install-banner-actions">
            <button className="install-banner-btn" onClick={handleInstallClick}>Installieren</button>
            <button className="install-banner-close" onClick={dismissInstallBanner} aria-label="Schließen">
              <XCircle size={18} />
            </button>
          </div>
        </div>
      )}

      {/* MOBILE MAP VIEW */}
      {location && mobileTab === 'map' && (
        <div className={`mobile-view-map visible`}>
          <div className="mobile-map-container-tab">
            {filteredStations.length > 0 ? (
              <>
                <MapContainer
                  center={[location.lat, location.lng]}
                  zoom={14}
                  className="mobile-map-fullscreen"
                  scrollWheelZoom={false}
                  touchZoom={true}
                  dragging={true}
                  doubleClickZoom={true}
                  zoomControl={true}
                  style={{ height: '100%', width: '100%' }}
                  key={`map-${mobileTab}`} // Force re-render when tab changes
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {filteredStations.map((st) => {
                    // Create custom HTML marker with all fuel prices for mobile
                    const customIcon = L.divIcon({
                      html: `
                        <div class="custom-marker mobile-marker ${favorites.includes(st.id) ? 'favorite' : ''}">
                          <div class="marker-pin">
                            <div class="marker-icon">⛽</div>
                          </div>
                          <div class="all-prices-label">
                            ${st.e5 > 0 ? `
                              <div class="fuel-price-item">
                                <span class="fuel-type">E5</span>
                                <span class="price">${st.e5.toFixed(3)}</span>
                              </div>
                            ` : ''}
                            ${st.e10 > 0 ? `
                              <div class="fuel-price-item">
                                <span class="fuel-type">E10</span>
                                <span class="price">${st.e10.toFixed(3)}</span>
                              </div>
                            ` : ''}
                            ${st.diesel > 0 ? `
                              <div class="fuel-price-item">
                                <span class="fuel-type">D</span>
                                <span class="price">${st.diesel.toFixed(3)}</span>
                              </div>
                            ` : ''}
                          </div>
                        </div>
                      `,
                      className: 'custom-marker-container mobile-marker-container',
                      iconSize: [70, 90],
                      iconAnchor: [35, 80],
                      popupAnchor: [0, -80]
                    });

                    return (
                      <Marker
                        key={st.id}
                        position={[st.lat, st.lng]}
                        icon={customIcon}
                      >
                        <Popup className="compact-popup mobile-popup" closeButton={false}>
                          <div className="compact-popup-content">
                            <div className="compact-header">
                              <strong className="station-name">{st.name}</strong>
                              <div className="station-distance">{st.dist} km</div>
                            </div>
                            
                            <div className="compact-address">
                              {st.street}, {st.place}
                            </div>

                            <div className="compact-prices">
                              {st.e5 > 0 && (
                                <div className="compact-fuel-row">
                                  <span className="fuel-label">E5</span>
                                  <span className="fuel-price">{st.e5.toFixed(3)}</span>
                                </div>
                              )}
                              {st.e10 > 0 && (
                                <div className="compact-fuel-row">
                                  <span className="fuel-label">E10</span>
                                  <span className="fuel-price">{st.e10.toFixed(3)}</span>
                                </div>
                              )}
                              {st.diesel > 0 && (
                                <div className="compact-fuel-row">
                                  <span className="fuel-label">Diesel</span>
                                  <span className="fuel-price">{st.diesel.toFixed(3)}</span>
                                </div>
                              )}
                            </div>

                            <div className="compact-actions">
                              <button
                                className="compact-btn navigation-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${st.lat},${st.lng}`, '_blank');
                                }}
                              >
                                <NavigationIcon size={15} /> Navigation
                              </button>
                              <button
                                className="compact-btn details-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  selectStation(st);
                                }}
                              >
                                <ClipboardList size={15} /> Details
                              </button>
                              {session && (
                                <button
                                  className={`compact-btn fav-btn ${favorites.includes(st.id) ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFavorite(st.id);
                                  }}
                                >
                                  <Star size={16} fill={favorites.includes(st.id) ? 'currentColor' : 'none'} />
                                </button>
                              )}
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                  <Marker position={[location.lat, location.lng]}>
                    <Popup>
                      <strong><MapPin size={13} style={{verticalAlign: 'middle', marginRight: 4}} />Ihr Standort</strong>
                    </Popup>
                  </Marker>
                  <Circle
                    key={`mobile-radius-${radius}`}
                    center={[location.lat, location.lng]}
                    radius={radius * 1000} // Convert km to meters
                    pathOptions={{
                      color: '#2563eb',
                      fillColor: '#2563eb',
                      fillOpacity: 0.1,
                      weight: 2,
                      dashArray: '5, 5'
                    }}
                  />
                  <MapViewUpdater 
                    center={[location.lat, location.lng]} 
                    zoom={13} 
                    locationKey={locationKey}
                  />
                  <DynamicRadiusUpdater onRadiusChange={handleDynamicRadiusChange} />
                </MapContainer>
                
                {/* Mobile Map Instructions */}
                <div className="mobile-map-instructions">
                  <span className="map-instruction-icon"><Hand size={16} /></span>
                  <span className="map-instruction-text">Verwenden Sie zwei Finger zum Bewegen und Zoomen der Karte</span>
                </div>
              </>
            ) : (
              <div className="mobile-empty-map">
                <div className="empty-map-icon"><Fuel size={32} /></div>
                <h3>Keine Tankstellen gefunden</h3>
                <p>Erweitern Sie den Suchradius</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MOBILE FAVORITES VIEW */}
      <div className={`mobile-view-favorites ${mobileTab === 'favorites' ? 'visible' : ''}`}>
        <div className="section-header">
          <h2 className="section-title"><Star size={20} fill="currentColor" /> Meine Favoriten</h2>
          <p>{favoriteStations.length} gespeicherte Tankstellen</p>
        </div>
        
        {favoriteStations.length > 0 ? (
          <div className="mobile-station-list">
            {favoriteStations.map((st) => (
              <div
                key={st.id}
                className="mobile-station-item"
                onClick={() => selectStation(st)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={() => handleTouchEnd(st.id)}
              >
                <div className="mobile-station-info-left">
                  <div className="mobile-station-name">{st.name}</div>
                  <div className="mobile-station-address">{st.street}, {st.place}</div>
                  <div className="mobile-distance-alert">
                    <span><Star size={13} fill="currentColor" /> Favorit</span>
                    {st.isOpen === false && <span className="mobile-closed-badge">Geschlossen</span>}
                  </div>
                </div>
                <div className="mobile-station-data-right">
                  <div className="mobile-fuel-prices">
                    {st.e5 > 0 && (
                      <div className="mobile-fuel-column">
                        <div className="mobile-fuel-label">E5</div>
                        <div className="mobile-fuel-price">{st.e5.toFixed(3)}</div>
                      </div>
                    )}
                    {st.e10 > 0 && (
                      <div className="mobile-fuel-column">
                        <div className="mobile-fuel-label">E10</div>
                        <div className="mobile-fuel-price">{st.e10.toFixed(3)}</div>
                      </div>
                    )}
                    {st.diesel > 0 && (
                      <div className="mobile-fuel-column">
                        <div className="mobile-fuel-label">D</div>
                        <div className="mobile-fuel-price">{st.diesel.toFixed(3)}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon"><Star size={28} /></div>
            <h3>Noch keine Favoriten</h3>
            <p>Markieren Sie Tankstellen als Favorit, um sie hier zu sehen.</p>
          </div>
        )}
      </div>

      {/* MAIN CONTENT (Search/Map) - Hidden if mobile tab is favorites or map */}
      <div className={`main-content-wrapper ${mobileTab === 'favorites' || mobileTab === 'map' ? 'hidden-mobile' : ''}`}>

        {/* ENHANCED LAYOUT - MAP & STATS FIRST ON DESKTOP */}
        {location && (
          <>
            {/* MAP & STATS CONTAINER - DESKTOP ONLY */}
            <div className="map-stats-container desktop-only">
              {/* PRICE STATISTICS SIDEBAR - 1/3 */}
              <div className="price-stats-sidebar">
                <div className="price-stats-banner">
                  <div className="banner-container">
                    <div className="banner-tabs">
                      <div className="tab-section">
                        <div className="tab-label">Bester Preis</div>
                        <div className="fuel-stats">
                          <div className="fuel-stat">
                            <span className="fuel-type">E5</span>
                            <span className="fuel-price">{priceStats.e5.min} €</span>
                          </div>
                          <div className="fuel-stat">
                            <span className="fuel-type">E10</span>
                            <span className="fuel-price">{priceStats.e10.min} €</span>
                          </div>
                          <div className="fuel-stat">
                            <span className="fuel-type">Diesel</span>
                            <span className="fuel-price">{priceStats.diesel.min} €</span>
                          </div>
                        </div>
                      </div>

                      <div className="tab-section">
                        <div className="tab-label">Durchschnitt</div>
                        <div className="fuel-stats">
                          <div className="fuel-stat">
                            <span className="fuel-type">E5</span>
                            <span className="fuel-price">{priceStats.e5.avg} €</span>
                          </div>
                          <div className="fuel-stat">
                            <span className="fuel-type">E10</span>
                            <span className="fuel-price">{priceStats.e10.avg} €</span>
                          </div>
                          <div className="fuel-stat">
                            <span className="fuel-type">Diesel</span>
                            <span className="fuel-price">{priceStats.diesel.avg} €</span>
                          </div>
                        </div>
                      </div>

                      <div className="tab-section">
                        <div className="tab-label">Teuerster</div>
                        <div className="fuel-stats">
                          <div className="fuel-stat">
                            <span className="fuel-type">E5</span>
                            <span className="fuel-price">{priceStats.e5.max} €</span>
                          </div>
                          <div className="fuel-stat">
                            <span className="fuel-type">E10</span>
                            <span className="fuel-price">{priceStats.e10.max} €</span>
                          </div>
                          <div className="fuel-stat">
                            <span className="fuel-type">Diesel</span>
                            <span className="fuel-price">{priceStats.diesel.max} €</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* MAP MAIN AREA - 2/3 */}
              <div className="map-enhanced-container">
                <div className="map-header-enhanced">
                  <div className="map-header-content">
                    <div className="map-header-info">
                      <h3><MapIcon size={18} style={{verticalAlign: 'middle', marginRight: 6}} />Tankstellen in Ihrer Umgebung</h3>
                      <p>{filteredStations.length} Tankstellen im Radius von {radius} km {radius >= 25 ? '(automatisch erweitert)' : ''}</p>
                    </div>
                    
                    {/* Radius Suggestion Integration - Inline */}
                    {suggestedRadius && suggestedRadius !== radius && (
                      <div className="radius-suggestion-inline-compact">
                        <span className="radius-suggestion-text-compact">
                          <Target size={14} style={{verticalAlign: 'middle', marginRight: 4}} />{suggestedRadius} km empfohlen
                        </span>
                        <button 
                          className="radius-suggestion-btn-compact"
                          onClick={acceptSuggestedRadius}
                          title={`Radius auf ${suggestedRadius} km ändern`}
                        >
                          <Check size={14} style={{verticalAlign: 'middle', marginRight: 4}} />Übernehmen
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {filteredStations.length > 0 ? (
                  <>
                    <MapContainer
                      center={[location.lat, location.lng]}
                      zoom={12}
                      className="map-enhanced"
                      scrollWheelZoom={true}
                      touchZoom={true}
                      dragging={true}
                      doubleClickZoom={true}
                      zoomControl={true}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      {filteredStations.map((st) => {
                        // Create custom HTML marker with all fuel prices
                        const customIcon = L.divIcon({
                          html: `
                            <div class="custom-marker ${favorites.includes(st.id) ? 'favorite' : ''}">
                              <div class="marker-pin">
                                <div class="marker-icon">⛽</div>
                              </div>
                              <div class="all-prices-label">
                                ${st.e5 > 0 ? `
                                  <div class="fuel-price-item">
                                    <span class="fuel-type">E5</span>
                                    <span class="price">${st.e5.toFixed(3)}</span>
                                  </div>
                                ` : ''}
                                ${st.e10 > 0 ? `
                                  <div class="fuel-price-item">
                                    <span class="fuel-type">E10</span>
                                    <span class="price">${st.e10.toFixed(3)}</span>
                                  </div>
                                ` : ''}
                                ${st.diesel > 0 ? `
                                  <div class="fuel-price-item">
                                    <span class="fuel-type">D</span>
                                    <span class="price">${st.diesel.toFixed(3)}</span>
                                  </div>
                                ` : ''}
                              </div>
                            </div>
                          `,
                          className: 'custom-marker-container',
                          iconSize: [80, 100],
                          iconAnchor: [40, 90],
                          popupAnchor: [0, -90]
                        });

                        return (
                          <Marker
                            key={st.id}
                            position={[st.lat, st.lng]}
                            icon={customIcon}
                          >
                            <Popup className="compact-popup" closeButton={false}>
                              <div className="compact-popup-content">
                                <div className="compact-header">
                                  <strong className="station-name">{st.name}</strong>
                                  <div className="station-distance">{st.dist} km</div>
                                </div>
                                
                                <div className="compact-address">
                                  {st.street}, {st.place}
                                </div>

                                <div className="compact-prices">
                                  {st.e5 > 0 && (
                                    <div className="compact-fuel-row">
                                      <span className="fuel-label">E5</span>
                                      <span className="fuel-price">{st.e5.toFixed(3)}</span>
                                    </div>
                                  )}
                                  {st.e10 > 0 && (
                                    <div className="compact-fuel-row">
                                      <span className="fuel-label">E10</span>
                                      <span className="fuel-price">{st.e10.toFixed(3)}</span>
                                    </div>
                                  )}
                                  {st.diesel > 0 && (
                                    <div className="compact-fuel-row">
                                      <span className="fuel-label">Diesel</span>
                                      <span className="fuel-price">{st.diesel.toFixed(3)}</span>
                                    </div>
                                  )}
                                </div>

                                <div className="compact-actions">
                                  <button
                                    className="compact-btn navigation-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(`https://www.google.com/maps/dir/?api=1&destination=${st.lat},${st.lng}`, '_blank');
                                    }}
                                  >
                                    <NavigationIcon size={14} /> Navigation
                                  </button>
                                  <button
                                    className="compact-btn details-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      selectStation(st);
                                    }}
                                  >
                                    <ClipboardList size={14} /> Details
                                  </button>
                                  {session && (
                                    <button
                                      className={`compact-btn fav-btn ${favorites.includes(st.id) ? 'active' : ''}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleFavorite(st.id);
                                      }}
                                    >
                                      <Star size={16} fill={favorites.includes(st.id) ? 'currentColor' : 'none'} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </Popup>
                          </Marker>
                        );
                      })}
                      <Marker position={[location.lat, location.lng]}>
                        <Popup>
                          <strong><MapPin size={13} style={{verticalAlign: 'middle', marginRight: 4}} />Ihr Standort</strong>
                        </Popup>
                      </Marker>
                      <Circle
                        key={`desktop-radius-${radius}`}
                        center={[location.lat, location.lng]}
                        radius={radius * 1000} // Convert km to meters
                        pathOptions={{
                          color: '#2563eb',
                          fillColor: '#2563eb',
                          fillOpacity: 0.1,
                          weight: 2,
                          dashArray: '5, 5'
                        }}
                      />
                      <MapViewUpdater 
                        center={[location.lat, location.lng]} 
                        zoom={12} 
                        locationKey={locationKey}
                      />
                      <DynamicRadiusUpdater onRadiusChange={handleDynamicRadiusChange} />
                    </MapContainer>
                  </>
                ) : (
                  <div className="empty-map-state">
                    <div className="empty-map-icon"><Fuel size={32} /></div>
                    <h3>Keine Tankstellen gefunden</h3>
                    <p>Erweitern Sie den Suchradius oder ändern Sie die Filtereinstellungen.</p>
                  </div>
                )}
              </div>
            </div>

            {/* ENHANCED DESKTOP FILTER & CONTROLS */}
            <section className="filter-panel-enhanced desktop-only">
              <div className="filter-header">
                <div className="filter-title-section">
                  <h3><SlidersHorizontal size={17} style={{verticalAlign: 'middle', marginRight: 6}} />Filter & Einstellungen</h3>
                  <div className="filter-subtitle">Passen Sie Ihre Suche an</div>
                </div>
                <div className="filter-stats">
                  <div className="filter-counter">{filteredStations.length} Stationen</div>
                  <div className="filter-radius-info">Radius: {radius} km</div>
                </div>
              </div>

              <div className="filter-content-grid">
                {/* RADIUS CONTROL SECTION */}
                <div className="filter-section">
                  <div className="filter-section-header">
                    <h4 className="filter-section-title"><Target size={15} /> Suchradius</h4>
                    <span className="filter-section-value">{tempRadius} km</span>
                  </div>
                  <div className="radius-control-enhanced">
                    <input
                      type="range"
                      min="1"
                      max="50"
                      value={tempRadius}
                      onChange={(e) => setTempRadius(parseInt(e.target.value))}
                      className="radius-slider-enhanced"
                    />
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={tempRadius}
                      onChange={(e) => setTempRadius(parseInt(e.target.value))}
                      className="radius-input-enhanced"
                    />
                    <button
                      className="update-radius-btn-enhanced"
                      onClick={() => setRadius(tempRadius)}
                    >
                      Aktualisieren
                    </button>
                  </div>
                </div>

                {/* SORTING SECTION */}
                <div className="filter-section">
                  <div className="filter-section-header">
                    <h4 className="filter-section-title"><BarChart3 size={15} /> Sortierung</h4>
                  </div>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="sort-select-enhanced"
                  >
                    <option value="relevance">Empfehlung (günstig &amp; nah)</option>
                    <option value="distance">Nach Entfernung</option>
                    <option value="price">Bester Preis (Alle)</option>
                    <option value="e5">Preis E5</option>
                    <option value="e10">Preis E10</option>
                    <option value="diesel">Preis Diesel</option>
                    <option value="name">Alphabetisch</option>
                  </select>
                </div>

                {/* FILTER OPTIONS SECTION */}
                <div className="filter-section">
                  <div className="filter-section-header">
                    <h4 className="filter-section-title"><SlidersHorizontal size={15} /> Filter</h4>
                  </div>
                  <div className="filter-options-grid">
                    <button
                      className={`filter-option-btn ${showOpenOnly ? 'active' : ''}`}
                      onClick={() => setShowOpenOnly(!showOpenOnly)}
                    >
                      <span className="filter-option-icon"><Clock size={14} /></span>
                      <span className="filter-option-text">Nur geöffnet</span>
                      <span className="filter-option-count">
                        {priceStats.openStations}
                      </span>
                    </button>
                    {session && favorites.length > 0 && (
                      <button
                        className={`filter-option-btn ${showFavorites ? 'active' : ''}`}
                        onClick={() => setShowFavorites(!showFavorites)}
                      >
                        <span className="filter-option-icon"><Star size={14} /></span>
                        <span className="filter-option-text">Favoriten</span>
                        <span className="filter-option-count">
                          {favorites.length}
                        </span>
                      </button>
                    )}
                  </div>
                </div>

              </div>
            </section>

            {/* SEARCH RESULTS HEADER - MODERN DASHBOARD */}
            <div className="results-header-modern">
              <div className="results-info-group">
                <h2 className="results-title"><Fuel size={19} /> Gefundene Tankstellen</h2>
                <div className="results-badges">
                  <span className="result-badge">{filteredStations.length} Stationen</span>
                  <span className="result-badge in-radius">in {radius} km</span>
                  <span className="result-badge open">{priceStats.openStations} geöffnet</span>
                </div>
              </div>

              <div className="cheapest-awards-container">
                {cheapestStations.e5 && (
                  <div className="cheapest-highlight-card e5" onClick={() => selectStation(cheapestStations.e5)}>
                    <div className="cheapest-label"><Trophy size={11} /> E5</div>
                    <div className="cheapest-name">{cheapestStations.e5.name}</div>
                    <div className="cheapest-price-display">{cheapestStations.e5.e5} €</div>
                  </div>
                )}
                {cheapestStations.e10 && (
                  <div className="cheapest-highlight-card e10" onClick={() => selectStation(cheapestStations.e10)}>
                    <div className="cheapest-label"><Trophy size={11} /> E10</div>
                    <div className="cheapest-name">{cheapestStations.e10.name}</div>
                    <div className="cheapest-price-display">{cheapestStations.e10.e10} €</div>
                  </div>
                )}
                {cheapestStations.diesel && (
                  <div className="cheapest-highlight-card diesel" onClick={() => selectStation(cheapestStations.diesel)}>
                    <div className="cheapest-label"><Trophy size={11} /> DIESEL</div>
                    <div className="cheapest-name">{cheapestStations.diesel.name}</div>
                    <div className="cheapest-price-display">{cheapestStations.diesel.diesel} €</div>
                  </div>
                )}
              </div>
            </div>

            {/* STATIONEN LISTE */}
            <div className="results-section">
              <>
                {/* NEW MOBILE-SPECIFIC DARK LIST (Visible only on mobile via CSS) */}
                <div className="mobile-results-container">
                  <header className="mobile-results-header">
                    <div className="mobile-results-title-row">
                      <button 
                        className="mobile-menu-btn"
                        onClick={() => setShowMobileFilters(!showMobileFilters)}
                      >
                        <Menu size={22} />
                      </button>
                      <h2 className="mobile-results-title">Ergebnisliste</h2>
                    </div>
                    <div className="mobile-header-actions">
                      <span className="mobile-action-icon"><MapIcon size={18} /></span>
                      <span className="mobile-action-icon">↓€</span>
                      <span className="mobile-action-icon">⋮</span>
                    </div>
                  </header>

                  <div className="mobile-subheader">
                    <span>{query || 'In der Nähe'}, {radius} km</span>
                    <span className="mobile-filter-icon"><Settings size={16} /></span>
                  </div>

                  {/* ENHANCED MOBILE FILTER PANEL */}
                  {showMobileFilters && (
                    <div className="mobile-filter-panel-enhanced">
                      {/* Quick Filter Chips */}
                      <div className="mobile-quick-filters">
                        <div className="quick-filter-chip-container">
                          <button
                            className={`quick-filter-chip ${showOpenOnly ? 'active' : ''}`}
                            onClick={() => setShowOpenOnly(!showOpenOnly)}
                          >
                            <span className="chip-icon"><Clock size={14} /></span>
                            <span className="chip-text">Nur geöffnet</span>
                            {showOpenOnly && <span className="chip-count">{priceStats.openStations}</span>}
                          </button>
                          {session && favorites.length > 0 && (
                            <button
                              className={`quick-filter-chip ${showFavorites ? 'active' : ''}`}
                              onClick={() => setShowFavorites(!showFavorites)}
                            >
                              <span className="chip-icon"><Star size={14} /></span>
                              <span className="chip-text">Favoriten</span>
                              <span className="chip-count">{favorites.length}</span>
                            </button>
                          )}
                          <button
                            className={`quick-filter-chip ${sortBy === 'price' ? 'active' : ''}`}
                            onClick={() => setSortBy('price')}
                          >
                            <span className="chip-icon"><Euro size={14} /></span>
                            <span className="chip-text">Günstigste</span>
                          </button>
                        </div>
                      </div>

                      {/* Radius Control Card */}
                      <div className="mobile-filter-card">
                        <div className="filter-card-header">
                          <h4 className="filter-card-title"><Target size={15} /> Suchradius</h4>
                          <span className="filter-card-value">{tempRadius} km</span>
                        </div>
                        <div className="mobile-radius-presets">
                          {[2, 5, 10, 20, 50].map(preset => (
                            <button
                              key={preset}
                              className={`radius-preset-btn ${tempRadius === preset ? 'active' : ''}`}
                              onClick={() => setTempRadius(preset)}
                            >
                              {preset}km
                            </button>
                          ))}
                        </div>
                        <div className="mobile-radius-control-enhanced">
                          <input
                            type="range"
                            min="1"
                            max="50"
                            value={tempRadius}
                            onChange={(e) => setTempRadius(parseInt(e.target.value))}
                            className="mobile-radius-slider-enhanced"
                          />
                          <div className="radius-display">
                            <span className="radius-min">1km</span>
                            <span className="radius-current">{tempRadius}km</span>
                            <span className="radius-max">50km</span>
                          </div>
                        </div>
                        {tempRadius !== radius && (
                          <button
                            className="mobile-apply-radius-btn"
                            onClick={() => setRadius(tempRadius)}
                          >
                            <Check size={15} style={{verticalAlign: 'middle', marginRight: 4}} />Radius anwenden
                          </button>
                        )}
                      </div>

                      {/* Sorting Card */}
                      <div className="mobile-filter-card">
                        <div className="filter-card-header">
                          <h4 className="filter-card-title"><BarChart3 size={15} /> Sortierung</h4>
                        </div>
                        <div className="mobile-sort-options">
                          {[
                            { value: 'relevance', icon: Target, label: 'Empfehlung (günstig & nah)' },
                            { value: 'distance', icon: Car, label: 'Entfernung' },
                            { value: 'price', icon: Euro, label: 'Bester Preis' },
                            { value: 'e5', icon: Fuel, label: 'E5 Preis' },
                            { value: 'e10', icon: Fuel, label: 'E10 Preis' },
                            { value: 'diesel', icon: Truck, label: 'Diesel Preis' },
                            { value: 'name', icon: Store, label: 'Name A-Z' }
                          ].map(option => (
                            <button
                              key={option.value}
                              className={`sort-option-btn ${sortBy === option.value ? 'active' : ''}`}
                              onClick={() => setSortBy(option.value)}
                            >
                              <span className="sort-icon"><option.icon size={16} /></span>
                              <span className="sort-label">{option.label}</span>
                              {sortBy === option.value && <span className="sort-check"><Check size={15} /></span>}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Filter Actions */}
                      <div className="mobile-filter-actions">
                        <button
                          className="filter-reset-btn"
                          onClick={() => {
                            setShowOpenOnly(false);
                            setShowFavorites(false);
                            setSortBy('relevance');
                            setTempRadius(5);
                            setRadius(5);
                          }}
                        >
                          <RotateCcw size={14} style={{verticalAlign: 'middle', marginRight: 4}} />Zurücksetzen
                        </button>
                        <button
                          className="filter-close-btn"
                          onClick={() => setShowMobileFilters(false)}
                        >
                          <Check size={15} style={{verticalAlign: 'middle', marginRight: 4}} />Fertig
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="mobile-station-list">
                    {filteredStations.length === 0 ? (
                      <div className="empty-state dark">
                        <p>Keine Tankstellen gefunden</p>
                      </div>
                    ) : (
                      filteredStations.map((st) => {
                        return (
                          <div key={st.id} className="mobile-station-item" onClick={() => selectStation(st)}>
                            <div className="mobile-station-info-left">
                              <h3 className="mobile-station-name">{st.name}</h3>
                              <p className="mobile-station-address">
                                {st.street} {st.houseNumber || ''}<br />
                                {st.postCode} {st.place}
                              </p>
                              <div className="mobile-distance-alert">
                                <span>{st.dist} km</span>
                                {st.isOpen === false && <span className="mobile-closed-badge">Geschlossen</span>}
                              </div>
                            </div>
                            <div className="mobile-station-data-right">
                              <div className="mobile-fuel-prices">
                                {st.e5 > 0 && (
                                  <div className="mobile-fuel-column">
                                    <span className="mobile-fuel-label">E5</span>
                                    <span className="mobile-fuel-price">{st.e5.toFixed(3)}</span>
                                  </div>
                                )}
                                {st.e10 > 0 && (
                                  <div className="mobile-fuel-column">
                                    <span className="mobile-fuel-label">E10</span>
                                    <span className="mobile-fuel-price">{st.e10.toFixed(3)}</span>
                                  </div>
                                )}
                                {st.diesel > 0 && (
                                  <div className="mobile-fuel-column">
                                    <span className="mobile-fuel-label">Diesel</span>
                                    <span className="mobile-fuel-price">{st.diesel.toFixed(3)}</span>
                                  </div>
                                )}
                              </div>
                              <span className="mobile-update-time">heute {new Date().getHours()}:{new Date().getMinutes().toString().padStart(2, '0')}</span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* ORIGINAL DESKTOP/TABLET GRID (Hidden on mobile via CSS) */}
                <main className="station-grid desktop-results">
                  {filteredStations.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-icon"><Fuel size={28} /></div>
                      <h3>Keine Tankstellen gefunden</h3>
                      <p>Erweitern Sie den Suchradius oder ändern Sie die Filter.</p>
                    </div>
                  ) : (
                    filteredStations.map((st, index) => (
                      <div
                        key={st.id}
                        className={`station-card-enhanced ${checkPriceAlert(st) ? 'price-alert' : ''} ${swipedStations.has(st.id) ? 'swiped' : ''} ${st.isOpen !== false ? 'station-open' : 'station-closed'} ${Object.values(cheapestStations).some(cs => cs && cs.id === st.id) ? 'cheapest' : ''}`}
                        onClick={() => selectStation(st)}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={() => handleTouchEnd(st.id)}
                      >
                        {/* Price Alert Badge */}
                        {checkPriceAlert(st) && (
                          <div className="price-alert-badge"><AlertTriangle size={13} /> Preisalarm</div>
                        )}

                        <div className="card-header">
                          <div className="station-header-left">
                            <h3 className="station-name">{st.name}</h3>
                            <div className="station-meta-items">
                              <div className="meta-item distance-item">
                                <span className="meta-icon"><Car size={13} /></span>
                                <span className="meta-text">{st.dist} km</span>
                              </div>
                              <div className="meta-item address-item">
                                <span className="meta-icon"><MapPin size={13} /></span>
                                <span className="meta-text">{st.street}, {st.postCode} {st.place}</span>
                              </div>
                            </div>
                            {cheapestStations.e5?.id === st.id && (
                              <div className="cheapest-badge e5"><Trophy size={11} /> E5</div>
                            )}
                            {cheapestStations.e10?.id === st.id && (
                              <div className="cheapest-badge e10"><Trophy size={11} /> E10</div>
                            )}
                            {cheapestStations.diesel?.id === st.id && (
                              <div className="cheapest-badge diesel"><Trophy size={11} /> DIESEL</div>
                            )}
                          </div>
                          {session && (
                            <button
                              className={`chart-icon-btn ${favorites.includes(st.id) ? 'favorite' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(st.id);
                              }}
                            >
                              <Star size={17} fill={favorites.includes(st.id) ? 'currentColor' : 'none'} />
                            </button>
                          )}
                        </div>

                        <div className="station-info">
                          <div className="station-meta">
                            <div className={`opening-status-small ${st.isOpen !== false ? 'status-open' : 'status-closed'}`}>
                              <div className={`status-dot-small ${st.isOpen !== false ? 'open' : 'closed'}`}></div>
                              <span className="status-text-small">{st.isOpen !== false ? 'Geöffnet' : 'Geschlossen'}</span>
                            </div>
                            {st.openingTimes && (
                              <div className="opening-times-small">
                                <Clock size={13} style={{verticalAlign: 'middle', marginRight: 4}} />{st.openingTimes}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="price-lines">
                          {st.e5 && st.e5 > 0 && (
                            <div className="price-line">
                              <span className="price-label-small">E5</span>
                              <span className="price-value-small">{st.e5} €</span>
                              {(() => {
                                const comparison = getPriceComparison(st.e5, 'e5');
                                return comparison && comparison.isCheaper && (
                                  <span className="price-comparison price-trend-down">
                                    ↓ {comparison.difference} € günstiger
                                  </span>
                                );
                              })()}
                            </div>
                          )}
                          {st.e10 && st.e10 > 0 && (
                            <div className="price-line">
                              <span className="price-label-small">E10</span>
                              <span className="price-value-small">{st.e10} €</span>
                              {(() => {
                                const comparison = getPriceComparison(st.e10, 'e10');
                                return comparison && comparison.isCheaper && (
                                  <span className="price-comparison price-trend-down">
                                    ↓ {comparison.difference} € günstiger
                                  </span>
                                );
                              })()}
                            </div>
                          )}
                          {st.diesel && st.diesel > 0 && (
                            <div className="price-line">
                              <span className="price-label-small">Diesel</span>
                              <span className="price-value-small">{st.diesel} €</span>
                              {(() => {
                                const comparison = getPriceComparison(st.diesel, 'diesel');
                                return comparison && comparison.isCheaper && (
                                  <span className="price-comparison price-trend-down">
                                    ↓ {comparison.difference} € günstiger
                                  </span>
                                );
                              })()}
                            </div>
                          )}
                        </div>

                        <div className="card-actions">
                          <button
                            className="card-btn navigation-primary full-width"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(`https://www.google.com/maps/dir/?api=1&destination=${st.lat},${st.lng}`, '_blank');
                            }}
                          >
                            <NavigationIcon size={16} /> Navigation starten
                          </button>
                          <button
                            className={`card-btn fav-action ${favorites.includes(st.id) ? 'active' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(st.id);
                            }}
                          >
                            <Star size={18} fill={favorites.includes(st.id) ? 'currentColor' : 'none'} />
                          </button>
                        </div>

                        {/* Swipe Indicator */}
                        <div className="swipe-indicator">
                          <span className="swipe-icon"><Star size={20} fill="currentColor" /></span>
                        </div>
                      </div>
                    ))
                  )}
                </main>
              </>
            </div>
          </>
        )}

        {/* Profile Modal, Settings Modal, etc. */}

        {/* PROFILE MODAL */}
        {showProfileModal && session && (
          <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
            <div className="modal-content profile-modal" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowProfileModal(false)}>×</button>

              <div className="profile-header">
                <div className="profile-avatar">
                  <img
                    src={session.user.user_metadata?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(session.user.email)}&background=2563eb&color=fff&size=64`}
                    alt="Avatar"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="avatar-fallback-large" style={{ display: 'none' }}>
                    {session.user.email.charAt(0).toUpperCase()}
                  </div>
                </div>
                <div className="profile-info">
                  <h3>{session.user.user_metadata?.full_name || 'Benutzer'}</h3>
                  <p>{session.user.email}</p>
                </div>
              </div>

              <div className="profile-stats">
                <div className="stat-item">
                  <span className="stat-number">{favorites.length}</span>
                  <span className="stat-label">Favoriten</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{favoriteStations.filter(s => s?.isOpen !== false).length}</span>
                  <span className="stat-label">Geöffnet</span>
                </div>
              </div>

              <div className="favorites-section">
                <h4><Star size={16} fill="currentColor" /> Meine Favoriten</h4>
                {favoriteStations.length === 0 ? (
                  <div className="no-favorites">
                    <p>Noch keine Favoriten gespeichert</p>
                    <small>Fügen Sie Tankstellen zu Ihren Favoriten hinzu, um sie hier zu sehen</small>
                  </div>
                ) : (
                  <div className="favorite-stations-list">
                    {favoriteStations.map((station) => (
                      <div key={station.id} className="favorite-station-item" onClick={() => {
                        setShowProfileModal(false);
                        selectStation(station);
                      }}>
                        <div className="favorite-station-info">
                          <h5>{station.name}</h5>
                          <p>{station.street}, {station.place}</p>
                          <div className="favorite-status">
                            <div className={`status-dot ${station.isOpen !== false ? 'open' : 'closed'}`}></div>
                            <span>{station.isOpen !== false ? 'Geöffnet' : 'Geschlossen'}</span>
                          </div>
                        </div>
                        <div className="favorite-prices">
                          {station.e5 && <span className="price-tag">E5: {station.e5}€</span>}
                          {station.e10 && <span className="price-tag">E10: {station.e10}€</span>}
                          {station.diesel && <span className="price-tag">Diesel: {station.diesel}€</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="profile-actions">
                <button className="action-btn secondary-btn" onClick={() => setShowSettingsModal(true)}>
                  <Settings size={16} /> Einstellungen
                </button>
                <button className="action-btn primary-btn" onClick={() => setShowProfileModal(false)}>
                  Schließen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* SETTINGS MODAL */}
        {showSettingsModal && session && (
          <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
            <div className="modal-content settings-modal" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowSettingsModal(false)}>×</button>

              <div className="settings-header">
                <h3><Settings size={18} /> Einstellungen</h3>
              </div>

              <div className="settings-section">
                <h4>Konto-Verwaltung</h4>
                <div className="setting-item">
                  <div className="setting-info">
                    <h5>Konto löschen</h5>
                    <p>Alle Daten werden unwiderruflich gelöscht</p>
                  </div>
                  <button className="danger-btn-small" onClick={async () => {
                    if (window.confirm('Möchten Sie Ihr Konto wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
                      try {
                        await supabase.from('favorites').delete().eq('user_id', session.user.id);
                        await supabase.auth.signOut();
                        setShowSettingsModal(false);
                        setShowProfileModal(false);
                        showToast('Konto wurde gelöscht', 'info');
                      } catch (error) {
                        showToast('Fehler beim Löschen des Kontos', 'error');
                      }
                    }
                  }}>
                    Löschen
                  </button>
                </div>
              </div>

              <div className="settings-actions">
                <button className="action-btn secondary-btn" onClick={handleLogout}>
                  <LogOut size={16} /> Abmelden
                </button>
                <button className="action-btn primary-btn" onClick={() => setShowSettingsModal(false)}>
                  Schließen
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="loading-overlay-enhanced">
            <div className="spinner"></div>
            <p className="loading-text">Lade Tankstellen...</p>
          </div>
        )}

        {/* COMPACT STATION DETAIL MODAL */}
        {selectedStation && (
          <div className="modal-overlay" onClick={closeStationDetail}>
            <div className="modal-content modern-station-modal" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={closeStationDetail}>×</button>

              {/* MODERN HEADER */}
              <div className="modern-modal-header">
                <div className="station-title-section">
                  <h3 className="station-name-modern">{selectedStation.name}</h3>
                  <div className="modal-header-meta">
                    {selectedStation.dist != null && (
                      <span className="station-distance-modern">
                        <MapPin size={14} /> {selectedStation.dist} km entfernt
                      </span>
                    )}
                    <span className={`opening-status-small ${selectedStation.isOpen !== false ? 'status-open' : 'status-closed'}`}>
                      <span className={`status-dot-small ${selectedStation.isOpen !== false ? 'open' : 'closed'}`}></span>
                      {selectedStation.isOpen !== false ? 'Geöffnet' : 'Geschlossen'}
                    </span>
                  </div>
                </div>
                {session && (
                  <button
                    className={`favorite-btn-modern ${favorites.includes(selectedStation.id) ? 'active' : ''}`}
                    onClick={() => toggleFavorite(selectedStation.id)}
                  >
                    <Star size={19} fill={favorites.includes(selectedStation.id) ? 'currentColor' : 'none'} />
                  </button>
                )}
              </div>

              {/* Scrollable body - header and actions stay fixed on mobile full-screen */}
              <div className="modern-modal-body">
              {/* MODERN PRICE TABLE */}
              <div className="modern-price-section">
                <h4 className="section-title"><Euro size={17} /> Aktuelle Preise</h4>
                <div className="modern-price-grid">
                  {selectedStation.e5 && selectedStation.e5 > 0 && (
                    <div className="price-row-modern">
                      <span className="fuel-type e5"><span className="fuel-dot" />E5</span>
                      <span className="fuel-price">{selectedStation.e5.toFixed(3)} €</span>
                      {(() => {
                        const comparison = getPriceComparison(selectedStation.e5, 'e5');
                        return comparison ? (
                          <span className={`price-indicator ${comparison.isCheaper ? 'good' : 'expensive'}`}>
                            {comparison.isCheaper ? <TrendingDown size={16} /> : <TrendingUp size={16} />}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  )}
                  {selectedStation.e10 && selectedStation.e10 > 0 && (
                    <div className="price-row-modern">
                      <span className="fuel-type e10"><span className="fuel-dot" />E10</span>
                      <span className="fuel-price">{selectedStation.e10.toFixed(3)} €</span>
                      {(() => {
                        const comparison = getPriceComparison(selectedStation.e10, 'e10');
                        return comparison ? (
                          <span className={`price-indicator ${comparison.isCheaper ? 'good' : 'expensive'}`}>
                            {comparison.isCheaper ? <TrendingDown size={16} /> : <TrendingUp size={16} />}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  )}
                  {selectedStation.diesel && selectedStation.diesel > 0 && (
                    <div className="price-row-modern">
                      <span className="fuel-type diesel"><span className="fuel-dot" />Diesel</span>
                      <span className="fuel-price">{selectedStation.diesel.toFixed(3)} €</span>
                      {(() => {
                        const comparison = getPriceComparison(selectedStation.diesel, 'diesel');
                        return comparison ? (
                          <span className={`price-indicator ${comparison.isCheaper ? 'good' : 'expensive'}`}>
                            {comparison.isCheaper ? <TrendingDown size={16} /> : <TrendingUp size={16} />}
                          </span>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* MODERN PRICE HISTORY CHART */}
              <div className="modern-chart-section">
                <h4 className="section-title"><TrendingUp size={17} /> Preisverlauf (7 Tage)</h4>
                {loadingHistory ? (
                  <div className="modern-loading">
                    <div className="loading-spinner"></div>
                    <span>Lade Preisverlauf...</span>
                  </div>
                ) : priceHistory.length > 0 ? (
                  <div className="modern-chart-container">
                    <ResponsiveContainer width="100%" height={150}>
                      <LineChart data={priceHistory}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="day" 
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: '#64748b' }}
                        />
                        <YAxis 
                          axisLine={false}
                          tickLine={false}
                          tick={{ fontSize: 12, fill: '#64748b' }}
                          tickFormatter={(value) => `${value}€`}
                          domain={['dataMin - 0.02', 'dataMax + 0.02']}
                          scale="linear"
                        />
                        <Tooltip content={<TankboardTooltip />} cursor={{ stroke: '#94a3b8', strokeDasharray: '3 3' }} />
                        {selectedStation.e5 && selectedStation.e5 > 0 && (
                          <Line 
                            type="monotone" 
                            dataKey="e5" 
                            stroke="#10b981" 
                            strokeWidth={2}
                            dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }}
                            activeDot={{ r: 5, stroke: '#10b981', strokeWidth: 2 }}
                          />
                        )}
                        {selectedStation.e10 && selectedStation.e10 > 0 && (
                          <Line 
                            type="monotone" 
                            dataKey="e10" 
                            stroke="#3b82f6" 
                            strokeWidth={2}
                            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 3 }}
                            activeDot={{ r: 5, stroke: '#3b82f6', strokeWidth: 2 }}
                          />
                        )}
                        {selectedStation.diesel && selectedStation.diesel > 0 && (
                          <Line 
                            type="monotone" 
                            dataKey="diesel" 
                            stroke="#f59e0b" 
                            strokeWidth={2}
                            dot={{ fill: '#f59e0b', strokeWidth: 2, r: 3 }}
                            activeDot={{ r: 5, stroke: '#f59e0b', strokeWidth: 2 }}
                          />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="no-chart-data">
                    <span><TrendingUp size={24} /></span>
                    <span>Keine Preishistorie verfügbar</span>
                  </div>
                )}
              </div>

              {/* MODERN STATION INFO */}
              {loadingDetails ? (
                <div className="modern-loading">
                  <div className="loading-spinner"></div>
                  <span>Lade Details...</span>
                </div>
              ) : stationDetails ? (
                <div className="modern-info-section">
                  <h4 className="section-title"><Info size={17} /> Station Details</h4>
                  <div className="info-grid-modern">
                    {stationDetails.brand && (
                      <div className="info-item-modern">
                        <span className="info-item-icon"><Store size={15} /></span>
                        <span className="info-item-text">
                          <span className="info-label">Marke</span>
                          <span className="info-value">{stationDetails.brand}</span>
                        </span>
                      </div>
                    )}
                    <div className="info-item-modern">
                      <span className="info-item-icon"><MapPin size={15} /></span>
                      <span className="info-item-text">
                        <span className="info-label">Adresse</span>
                        <span className="info-value">
                          {stationDetails.street} {stationDetails.houseNumber}<br/>
                          {stationDetails.postCode} {stationDetails.place}
                        </span>
                      </span>
                    </div>
                    {stationDetails.firstActive && (
                      <div className="info-item-modern">
                        <span className="info-item-icon"><Calendar size={15} /></span>
                        <span className="info-item-text">
                          <span className="info-label">Aktiv seit</span>
                          <span className="info-value">
                            {new Date(stationDetails.firstActive).toLocaleDateString('de-DE')}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="modern-info-section">
                  <div className="info-error"><AlertTriangle size={15} /> Details konnten nicht geladen werden</div>
                </div>
              )}

              {/* MODERN OPENING TIMES */}
              {stationDetails && stationDetails.openingTimes && stationDetails.openingTimes.length > 0 && (
                <div className="modern-hours-section">
                  <div className="hours-section-header">
                    <h4 className="section-title"><Clock size={17} /> Öffnungszeiten</h4>
                    <span className={`opening-status-small ${selectedStation.isOpen !== false ? 'status-open' : 'status-closed'}`}>
                      <span className={`status-dot-small ${selectedStation.isOpen !== false ? 'open' : 'closed'}`}></span>
                      {selectedStation.isOpen !== false ? 'Jetzt geöffnet' : 'Jetzt geschlossen'}
                    </span>
                  </div>
                  <div className="hours-grid-modern">
                    {stationDetails.openingTimes.map((time, index) => {
                      const isToday = isCurrentDay(time.text || time.day);
                      return (
                        <div
                          key={index}
                          className={`hours-row-modern ${isToday ? 'current-day' : ''}`}
                        >
                          <span className="day-name">
                            {isToday && <CalendarClock size={13} />}
                            {time.text || time.day || `Tag ${index + 1}`}
                          </span>
                          <span className="hours-time">
                            {time.start && time.end ? `${time.start} - ${time.end}` :
                             time.hours ? time.hours : 'Geschlossen'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              </div>

              {/* MODERN ACTIONS SECTION */}
              <div className="modern-actions-section">
                <div className="modern-actions-row">
                  <button
                    className="modern-action-btn"
                    onClick={() => {
                      window.open(`https://www.google.com/maps/dir/?api=1&destination=${selectedStation.lat},${selectedStation.lng}`, '_blank');
                    }}
                  >
                    <NavigationIcon size={16} /> Navigation starten
                  </button>
                  <button
                    className="modern-action-btn secondary"
                    onClick={() => shareStation(selectedStation)}
                    title="Tankstelle teilen"
                  >
                    <Share2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FAVORITEN OHNE STANDORT */}
        {!location && session && favoriteStations.length > 0 && (
          <section className="favorites-without-location">
            <div className="section-header">
              <h2 className="section-title"><Star size={20} fill="currentColor" /> Meine Favoriten</h2>
              <p>{favoriteStations.length} gespeicherte Tankstellen</p>
            </div>

            <div className="station-grid desktop-favorites">
              {favoriteStations.map((st, index) => (
                <div
                  key={st.id}
                  className="station-card-enhanced"
                  onClick={() => selectStation(st)}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="card-header">
                    <div className="station-header-left">
                      <h3 className="station-name">{st.name}</h3>
                      <div className="station-meta-items">
                        <div className="meta-item favorite-item">
                          <span className="meta-icon"><Star size={13} fill="currentColor" /></span>
                          <span className="meta-text">Favorit</span>
                        </div>
                        <div className="meta-item address-item">
                          <span className="meta-icon"><MapPin size={13} /></span>
                          <span className="meta-text">{st.street}, {st.place}</span>
                        </div>
                      </div>
                    </div>
                    <button
                      className="chart-icon-btn favorite"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(st.id);
                      }}
                    >
                      <Star size={20} fill="currentColor" />
                    </button>
                  </div>

                  <div className="station-info">
                    <div className="station-meta">
                      <div className="opening-status-small">
                        <div className={`status-dot-small ${st.isOpen !== false ? 'open' : 'closed'}`}></div>
                        <span className="status-text-small">{st.isOpen !== false ? 'Geöffnet' : 'Geschlossen'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="price-lines">
                    {st.e5 && st.e5 > 0 && (
                      <div className="price-line">
                        <span className="price-label-small">E5</span>
                        <span className="price-value-small">{st.e5} €</span>
                      </div>
                    )}
                    {st.e10 && st.e10 > 0 && (
                      <div className="price-line">
                        <span className="price-label-small">E10</span>
                        <span className="price-value-small">{st.e10} €</span>
                      </div>
                    )}
                    {st.diesel && st.diesel > 0 && (
                      <div className="price-line">
                        <span className="price-label-small">Diesel</span>
                        <span className="price-value-small">{st.diesel} €</span>
                      </div>
                    )}
                  </div>

                  <div className="card-actions">
                    <button
                      className="card-btn navigation-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`https://www.google.com/maps/dir/?api=1&destination=${st.lat},${st.lng}`, '_blank');
                      }}
                    >
                      <NavigationIcon size={15} /> Navigation starten
                    </button>
                    <button
                      className="card-btn secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        selectStation(st);
                      }}
                    >
                      <ClipboardList size={15} /> Details
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Mobile View - Same design as search results */}
            <div className="mobile-station-list show-mobile">
              {favoriteStations.map((st) => (
                <div
                  key={st.id}
                  className="mobile-station-item"
                  onClick={() => selectStation(st)}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={() => handleTouchEnd(st.id)}
                >
                  <div className="mobile-station-info-left">
                    <div className="mobile-station-name">{st.name}</div>
                    <div className="mobile-station-address">{st.street}, {st.place}</div>
                    <div className="mobile-distance-alert">
                      <span><Star size={13} fill="currentColor" /> Favorit</span>
                      {st.isOpen === false && <span className="mobile-closed-badge">Geschlossen</span>}
                    </div>
                  </div>
                  <div className="mobile-station-data-right">
                    <div className="mobile-fuel-prices">
                      {st.e5 > 0 && (
                        <div className="mobile-fuel-column">
                          <div className="mobile-fuel-label">E5</div>
                          <div className="mobile-fuel-price">{st.e5.toFixed(3)}</div>
                        </div>
                      )}
                      {st.e10 > 0 && (
                        <div className="mobile-fuel-column">
                          <div className="mobile-fuel-label">E10</div>
                          <div className="mobile-fuel-price">{st.e10.toFixed(3)}</div>
                        </div>
                      )}
                      {st.diesel > 0 && (
                        <div className="mobile-fuel-column">
                          <div className="mobile-fuel-label">D</div>
                          <div className="mobile-fuel-price">{st.diesel.toFixed(3)}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* WELCOME SCREEN */}
        {
          !location && (!session || favorites.length === 0) && (
            <div className="welcome-screen">
              <div className="welcome-content">
                <div className="hero-section">
                  <div className="hero-badge">
                    <span className="badge-icon"><Zap size={13} /></span>
                    <span className="badge-text">Deutschlands #1 Benzinpreis-Vergleich</span>
                  </div>

                  {/* H1 trägt die eigentlichen Suchbegriffe. Ein Crawler hat nie
                      einen Standort, sieht also immer diesen Zustand - was hier
                      steht, ist die für Google sichtbare Hauptüberschrift. */}
                  <h1 className="hero-title">
                    Benzinpreise vergleichen und bis zu{' '}
                    <span className="highlight">20 Cent</span> pro Liter sparen
                  </h1>

                  <p className="hero-subtitle">
                    Aktuelle Spritpreise für Super E10, Super E5 und Diesel von über 14.000
                    Tankstellen in Deutschland – in Echtzeit verglichen.
                  </p>

                  <div className="trust-indicators">
                    <div className="trust-item">
                      <span className="trust-number">14.000+</span>
                      <span className="trust-label">Tankstellen</span>
                    </div>
                    <div className="trust-divider" />
                    <div className="trust-item">
                      <span className="trust-number">100%</span>
                      <span className="trust-label">Kostenlos</span>
                    </div>
                    <div className="trust-divider" />
                    <div className="trust-item">
                      <span className="trust-number">24/7</span>
                      <span className="trust-label">Aktuell</span>
                    </div>
                  </div>
                </div>

                <div className="how-it-works">
                  <div className="how-step">
                    <div className="how-step-number">1</div>
                    <h4>Standort eingeben</h4>
                    <p>Adresse, PLZ oder Stadt eingeben — oder Ihren GPS-Standort nutzen</p>
                  </div>
                  <div className="how-step-arrow">→</div>
                  <div className="how-step">
                    <div className="how-step-number">2</div>
                    <h4>Preise vergleichen</h4>
                    <p>Alle Tankstellen in der Nähe auf einen Blick, sortiert nach Preis</p>
                  </div>
                  <div className="how-step-arrow">→</div>
                  <div className="how-step">
                    <div className="how-step-number">3</div>
                    <h4>Günstig tanken</h4>
                    <p>Zur günstigsten Tankstelle navigieren und bares Geld sparen</p>
                  </div>
                </div>

                <h2 className="section-heading">Warum TANKGÜNSTIG?</h2>
                <div className="features-grid">
                  <div className="feature-card">
                    <div className="feature-icon"><Target size={24} /></div>
                    <div className="feature-content">
                      <h3>Präzise Suche</h3>
                      <p>Finden Sie die günstigsten Tankstellen in Ihrer Nähe</p>
                    </div>
                  </div>

                  <div className="feature-card">
                    <div className="feature-icon"><Euro size={24} /></div>
                    <div className="feature-content">
                      <h3>Geld sparen</h3>
                      <p>Bis zu 240€ pro Jahr durch cleveres Tanken</p>
                    </div>
                  </div>

                  <div className="feature-card">
                    <div className="feature-icon"><Smartphone size={24} /></div>
                    <div className="feature-content">
                      <h3>Echtzeit-Daten</h3>
                      <p>Aktuelle Preise direkt von der MTS-K</p>
                    </div>
                  </div>

                  <div className="feature-card">
                    <div className="feature-icon"><TrendingUp size={24} /></div>
                    <div className="feature-content">
                      <h3>Preis-Historie</h3>
                      <p>Trends analysieren und zum besten Zeitpunkt tanken</p>
                    </div>
                  </div>
                </div>

                <div className="cta-section">
                  <button className="cta-primary" onClick={getBrowserLocation}>
                    <span className="cta-icon"><MapPin size={17} /></span>
                    <span className="cta-text">
                      <span className="cta-main">Jetzt Standort finden</span>
                      <span className="cta-sub">Sofort günstig tanken</span>
                    </span>
                  </button>

                  <div className="quick-search">
                    <span className="quick-label">Oder schnell suchen:</span>
                    <div className="quick-cities">
                      {lastSearchedPostalCode ? (
                        <button
                          className="city-chip"
                          onClick={() => {
                            const fakeSearch = {
                              lat: '0',
                              lon: '0',
                              display_name: lastSearchedPostalCode
                            };
                            fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(lastSearchedPostalCode)}&countrycodes=de&limit=1`)
                              .then(res => res.json())
                              .then(data => {
                                if (data.length > 0) {
                                  selectSuggestion(data[0]);
                                }
                              });
                          }}
                        >
                          {lastSearchedPostalCode}
                        </button>
                      ) : null}
                      {['Berlin', 'Hamburg', 'München', 'Köln', 'Frankfurt'].map(city => (
                        <button
                          key={city}
                          className="city-chip"
                          onClick={() => {
                            fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(city)}&countrycodes=de&limit=1`)
                              .then(res => res.json())
                              .then(data => {
                                if (data.length > 0) {
                                  selectSuggestion(data[0]);
                                }
                              });
                          }}
                        >
                          {city}
                        </button>
                      ))}
                    </div>
                  </div>

                  {recentSearches.length > 0 && (
                    <div className="recent-searches-section">
                      <span className="recent-label">Zuletzt gesucht:</span>
                      <div className="recent-cities">
                        {recentSearches.slice(0, 3).map((search, index) => {
                          const cityMatch = search.display_name.match(/^([^,]+)/);
                          const postalMatch = search.display_name.match(/\b\d{5}\b/);
                          const cityName = cityMatch ? cityMatch[1].trim() : 'Ort';
                          const searchQuery = postalMatch ? postalMatch[0] : search.display_name;

                          return (
                            <button
                              key={search.place_id || index}
                              className="recent-chip"
                              onClick={() => {
                                selectSuggestion(search);
                              }}
                            >
                              {cityName}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <FaqSection />
              </div>
            </div>
          )
        }

        {/* TOAST NOTIFICATION */}
        {
          toast && (
            <div className={`toast-enhanced toast-${toast.type}`}>
              <div className="toast-content-enhanced">
                <span className="toast-icon-enhanced">
                  {toast.type === 'success' ? <CheckCircle2 size={18} /> : toast.type === 'error' ? <XCircle size={18} /> : <Info size={18} />}
                </span>
                <span className="toast-message-enhanced">{toast.message}</span>
              </div>
            </div>
          )
        }

        {/* SEO FOOTER */}
        <footer className="seo-footer">
          <div className="footer-content">
            <div className="footer-section">
              <h3>Über TANKGÜNSTIG</h3>
              <p>
                TANKGÜNSTIG ist Deutschlands modernster Benzinpreis-Vergleich. Wir helfen Ihnen dabei,
                die günstigsten Tankstellen in Ihrer Umgebung zu finden und bares Geld zu sparen.
                Unsere Daten werden in Echtzeit von der offiziellen Markttransparenzstelle für Kraftstoffe (MTS-K) bezogen.
              </p>
            </div>

            <div className="footer-section">
              <h3>Kraftstoffpreise vergleichen</h3>
              <p>
                Vergleichen Sie aktuelle Preise für Super E5, Super E10 und Diesel von über 14.000
                Tankstellen deutschlandweit. Mit unserem intelligenten Preisvergleich sparen Sie bis zu
                20 Cent pro Liter und finden immer die günstigste Tankstelle in Ihrer Nähe.
              </p>
            </div>

            <div className="footer-section">
              <h3>Geld sparen beim Tanken</h3>
              <p>
                Speichern Sie Ihre Lieblings-Tankstellen als Favoriten
                und planen Sie Ihre Route zur günstigsten Tankstelle. TANKGÜNSTIG macht Spritpreise
                transparent und hilft Ihnen dabei, jeden Monat Geld zu sparen.
              </p>
            </div>

            <div className="footer-section">
              <h3>Ratgeber</h3>
              <ul className="footer-link-list">
                <li><Link to="/blog/warum-sind-die-spritpreise-2026-so-hoch">Warum die Spritpreise 2026 so hoch sind</Link></li>
                <li><Link to="/blog/beste-uhrzeit-zum-tanken">Beste Uhrzeit zum Tanken</Link></li>
                <li><Link to="/blog/sprit-sparen-tipps-alltag">12 Tipps zum Sprit sparen</Link></li>
                <li><Link to="/blog">Alle Artikel</Link></li>
              </ul>
            </div>
          </div>

          {/* Städte-Verlinkung: macht die Landingpages für Suchmaschinen
              auffindbar und deckt Suchanfragen wie "benzinpreise berlin" ab */}
          <nav className="footer-cities" aria-label="Benzinpreise nach Stadt">
            <h3>Benzinpreise nach Stadt</h3>
            <ul>
              {topCities.map((city) => (
                <li key={city.slug}>
                  <Link to={`/benzinpreise/${city.slug}`}>Benzinpreise {city.name}</Link>
                </li>
              ))}
              <li><Link to="/benzinpreise">Alle Städte →</Link></li>
            </ul>
          </nav>

          <div className="footer-bottom">
            <p>© {new Date().getFullYear()} TANKGÜNSTIG - Benzinpreise vergleichen und sparen</p>
            <div className="footer-links">
              <Link to="/blog" className="legal-link">Ratgeber &amp; News</Link>
              <Link to="/impressum" className="legal-link">Impressum</Link>
              <Link to="/datenschutz" className="legal-link">Datenschutz</Link>
              <button 
                className="legal-link cookie-trigger-btn" 
                onClick={() => {
                  if (window.Cookiebot) {
                    window.Cookiebot.show();
                  }
                }}
              >
                Cookie-Einstellungen
              </button>
            </div>
            <div className="footer-keywords">
              <span>Benzinpreise</span>
              <span>Dieselpreise</span>
              <span>Tankstellen finden</span>
              <span>Spritpreise vergleichen</span>
              <span>Günstig tanken</span>
              <span>Preisvergleich</span>
              <span>Tankerkönig</span>
              <span>MTS-K</span>
            </div>
          </div>
        </footer>
      </div> {/* End main-content-wrapper */}

      {/* MOBILE BOTTOM NAVIGATION */}
      <nav className="mobile-bottom-nav">
        <button
          className={`nav-item ${mobileTab === 'search' ? 'active' : ''}`}
          onClick={() => setMobileTab('search')}
        >
          <span className="nav-icon"><Search size={22} /></span>
          <span className="nav-label">Suche</span>
        </button>
        {location && (
          <button
            className={`nav-item ${mobileTab === 'map' ? 'active' : ''}`}
            onClick={() => setMobileTab('map')}
          >
            <span className="nav-icon"><MapIcon size={22} /></span>
            <span className="nav-label">Karte</span>
          </button>
        )}
        <button
          className={`nav-item ${mobileTab === 'favorites' ? 'active' : ''}`}
          onClick={() => setMobileTab('favorites')}
        >
          <span className="nav-icon"><Star size={22} fill={mobileTab === 'favorites' ? 'currentColor' : 'none'} /></span>
          <span className="nav-label">Favoriten</span>
        </button>
        {session ? (
          <button
            className={`nav-item ${mobileTab === 'profile' ? 'active' : ''}`}
            onClick={() => {
              setMobileTab('profile');
              setShowProfileModal(true);
            }}
          >
            <div className="nav-avatar">
              {session.user.user_metadata?.avatar_url ? (
                <img src={session.user.user_metadata.avatar_url} alt="Profil" />
              ) : (
                <span className="nav-icon"><User size={22} /></span>
              )}
            </div>
            <span className="nav-label">Profil</span>
          </button>
        ) : (
          <button
            className={`nav-item ${mobileTab === 'profile' ? 'active' : ''}`}
            onClick={() => handleLogin()}
          >
            <span className="nav-icon"><LogIn size={22} /></span>
            <span className="nav-label">Anmelden</span>
          </button>
        )}
      </nav>

    </div>
  );
}

// Main App component with Router
function App() {
  return (
    <Router>
      <Suspense fallback={<div className="route-loading">Wird geladen …</div>}>
        <Routes>
          <Route path="/" element={<MainApp />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/benzinpreise" element={<CityIndex />} />
          <Route path="/benzinpreise/:slug" element={<CityPage />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;